<template>
  <RevAccordionItem>
    <template #prefix>
      <IconLockLocked />
    </template>
    <template #title>
      <span
        :class="{
          'text-static-success-hi': !insuranceRequireAttention,
          'text-static-danger-hi': insuranceRequireAttention,
        }"
      >
        {{ i18n(translations.title) }}
      </span>
    </template>
    <template #description>
      <div data-test="description">
        <div v-if="firstPolicy">
          <span v-if="firstPolicy.state === 'CREATED'">
            {{ i18n(translations.protectionPlanAwaitingConfirmation) }}
          </span>
          <span
            v-if="
              firstPolicy.state === 'SUBSCRIBED' && firstPolicy.expirationDate
            "
            data-allow-mismatch="text"
          >
            {{
              i18n(translations.protectionPlanSubscribedUntil, {
                endDate: new Date(firstPolicy.expirationDate),
              })
            }}
          </span>
          <span
            v-if="
              firstPolicy.state === 'ON_HOLD' && firstPolicy.maxDuePaymentDate
            "
            data-allow-mismatch="text"
          >
            {{
              i18n(translations.protectionPlanNeedsPaymentBy, {
                date: new Date(firstPolicy.maxDuePaymentDate),
              })
            }}
          </span>
          <span
            v-if="
              (firstPolicy.state === 'TERMINATED' ||
                firstPolicy.state === 'EXPIRED') &&
              firstPolicy.terminationDate
            "
            data-allow-mismatch="text"
          >
            {{
              i18n(translations.protectionPlanTerminatedWithDates, {
                date: new Date(firstPolicy.terminationDate),
              })
            }}
          </span>
        </div>
        <span v-else>
          {{
            i18n(translations.description, {
              count: activeInsurancesCount,
            })
          }}
        </span>
      </div>
    </template>
    <template #body>
      <p v-if="!chillpack && !trustpack && isEmpty(policies)">
        {{ i18n(translations.noInsurancesText) }}
      </p>
      <ul v-else class="space-y-16" data-test="insurances">
        <li v-if="chillpack">
          <p class="body-2-bold">{{ i18n(translations.chillpackTitle) }}</p>

          {{
            i18n(translations.chillpack, {
              duration: chillpack.duration,
              date: new Date(chillpack.endDate),
            })
          }}
        </li>
        <li v-if="trustpack">
          <p class="body-2-bold">{{ i18n(translations.trustpackTitle) }}</p>

          {{
            i18n(translations.trustpack, {
              duration: trustpack.duration,
              date: new Date(trustpack.endDate),
            })
          }}
        </li>
        <li v-for="policy in policies" :key="policy.id">
          <p class="body-2-bold">
            {{
              i18n(translations.coverageAndPartnerName, {
                coverageName: policy.offerTitle,
                partnerName: policy.partnerName,
              })
            }}
          </p>
          <p v-if="policy.state === 'CREATED'">
            {{ i18n(translations.policyDatesNotAvailable) }}
          </p>
          <p
            v-if="
              (policy.state === 'SUBSCRIBED' || policy.state === 'ON_HOLD') &&
              policy.subscriptionDate &&
              policy.expirationDate
            "
          >
            {{
              i18n(translations.policyStartAndEndDate, {
                startDate: new Date(policy.subscriptionDate),
                endDate: new Date(policy.expirationDate),
              })
            }}
          </p>
          <p
            v-if="
              (policy.state === 'TERMINATED' || policy.state === 'EXPIRED') &&
              policy.subscriptionDate &&
              policy.expirationDate
            "
          >
            {{
              i18n(translations.policyStartAndEndDate, {
                startDate: new Date(policy.subscriptionDate),
                endDate: new Date(policy.expirationDate),
              })
            }}
          </p>
        </li>
      </ul>
    </template>
  </RevAccordionItem>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { isFutureDate } from '@backmarket/utils/date/isFutureDate'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevAccordionItem } from '@ds/components/AccordionItem'
import { IconLockLocked } from '@ds/icons/IconLockLocked'

import type { Coverage } from '~/api/care-folders/types/care-folder'

import translations from './Insurance.translations'

type Policy = Coverage['insurancePolicies'][number]
type ChillpackInsurance = Coverage['chillpackInsurance']
type TrustpackInsurance = Coverage['trustpackInsurance']

const { policies, chillpack, trustpack } = defineProps<{
  policies?: Policy[]
  chillpack?: ChillpackInsurance
  trustpack?: TrustpackInsurance
}>()

const firstPolicy = computed(() => (policies ? policies[0] : null))

function isInsuranceActive(
  insurance?: ChillpackInsurance | TrustpackInsurance,
) {
  if (!insurance) return false

  return isFutureDate(insurance.endDate)
}

function isPolicyActive(policy: Policy) {
  return (
    policy.state === 'CREATED' ||
    policy.state === 'SUBSCRIBED' ||
    policy.state === 'ON_HOLD'
  )
}

const activePolicies = computed(() => policies?.filter(isPolicyActive))

const activeInsurancesCount = computed(() => {
  let count = 0

  if (isInsuranceActive(chillpack)) count += 1
  if (isInsuranceActive(trustpack)) count += 1

  if (activePolicies.value) count += activePolicies.value.length

  return count
})

const onHoldPoliciesCount = computed(
  () =>
    policies?.filter((policy: Policy) => policy.state === 'ON_HOLD').length ||
    0,
)

const insuranceRequireAttention = computed(
  () => onHoldPoliciesCount.value > 0 || activeInsurancesCount.value === 0,
)

const i18n = useI18n()
</script>
